import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Check, X, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  pending: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  draft: "bg-slate-500/15 text-slate-300 border border-slate-500/30",
};

export default function ManagedListingsPanel({ onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null); // {id, name}
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/managed-listings");
      setItems(data.items || []);
      onCountChange?.(data.counts?.pending || 0);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/listings/${id}/approve`);
      toast.success("Listing approved — advertiser notified by email");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const confirmReject = async () => {
    const id = rejectTarget.id;
    setBusy(id);
    try {
      await api.post(`/admin/listings/${id}/reject`, { reason: reason.trim() });
      toast.success("Listing rejected — advertiser notified by email");
      setRejectTarget(null); setReason("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const openReject = (l) => { setRejectTarget({ id: l.id, name: l.advertiser_name || l.business_name || "this listing" }); setReason(""); };

  const pending = items.filter((i) => i.status === "pending");
  const others = items.filter((i) => i.status !== "pending");

  return (
    <div className="space-y-4" data-testid="managed-listings-panel">
      <div>
        <h2 className="font-heading font-bold text-xl">Managed-Zone Listings</h2>
        <p className="text-sm text-muted-foreground">Advertisers in Velopaths-managed zones have no partner shop, so Velopaths reviews their listings here. The advertiser is emailed the moment you approve or reject.</p>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="managed-listings-empty">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-heading font-bold">No managed-zone listings</div>
          <p className="text-sm text-muted-foreground mt-1">Advertisers who sign up inside a Velopaths-managed zone will appear here for approval.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Business</TableHead><TableHead>Zone</TableHead>
              <TableHead>Status</TableHead><TableHead>Subscription</TableHead><TableHead className="text-right">Review</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {[...pending, ...others].map((l) => (
                <TableRow key={l.id} data-testid={`managed-listing-${l.id}`}>
                  <TableCell className="font-medium">{l.advertiser_name || l.business_name || "—"}</TableCell>
                  <TableCell>{l.zone_name || "—"}</TableCell>
                  <TableCell><Badge className={STATUS_CLS[l.status] || ""} data-testid={`managed-listing-status-${l.id}`}>{l.status}</Badge></TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{l.subscription_status}</span></TableCell>
                  <TableCell className="text-right">
                    {l.status === "pending" ? (
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" disabled={busy === l.id} onClick={() => approve(l.id)} data-testid={`managed-listing-approve-${l.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          {busy === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Approve
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy === l.id} onClick={() => openReject(l)} data-testid={`managed-listing-reject-${l.id}`} className="text-rose-400">
                          <X className="w-4 h-4 mr-1" />Reject
                        </Button>
                      </div>
                    ) : l.status === "approved" ? (
                      <Button size="sm" variant="ghost" disabled={busy === l.id} onClick={() => openReject(l)} data-testid={`managed-listing-reject-${l.id}`} className="text-rose-400">Revoke</Button>
                    ) : (
                      <Button size="sm" variant="ghost" disabled={busy === l.id} onClick={() => approve(l.id)} data-testid={`managed-listing-approve-${l.id}`} className="text-emerald-400">Approve</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setReason(""); } }}>
        <DialogContent data-testid="managed-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject listing for {rejectTarget?.name}</DialogTitle>
            <DialogDescription>The advertiser will be emailed by Velopaths. Add an optional reason they'll see so they know what to fix.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="e.g. Please add a valid business website and a clearer photo before resubmitting." data-testid="managed-reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRejectTarget(null); setReason(""); }} data-testid="managed-reject-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy === rejectTarget?.id} onClick={confirmReject} data-testid="managed-reject-confirm">
              {busy === rejectTarget?.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
